import * as React from 'react';
import { connect } from 'react-redux';
import { flow, forEach, get, find} from 'lodash';
import { FormattedNumber } from 'react-intl';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import { IBrandingState, II18nMessages } from '../../../../services/branding/models';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { Field, FieldArray, formValueSelector, InjectedFormProps, reduxForm, submit } from 'redux-form';
import {
  isRequired,
  isPostcode, isEmail, isPhone,
} from '../../../../util/validators';
import { IChannel, ICustomer, IPaymentInfo, IPremium, IProduct, IPostcodeAddress } from '../../../../data/api/models';
import { IStore } from '../../../../redux/IStore';
import { actions } from '../../../../services/quote/reducer';
import { IQuoteState } from '../../../../services/quote/models';
import Travellers from '../TripDetails/components/Travellers';
import FieldFormControl from '../../../../components/FieldFormControl';
import MarketingOptions from '../../components/MarketingOptions';
import PostcodeControl from '../../../../components/PostcodeControl';
import {scrollToFirstError} from '../../../../util/scrollToFirstError';

interface IPaymentFormProps extends InjectedFormProps {
  i18n: II18nMessages;
  product: IProduct;
  onBack: () => void;
  premium: IPremium;
  renderDefinition(name: string): any;
  channel: IChannel;
  branding: IBrandingState;
  customer: ICustomer;
  purchaseDeclaration: boolean;
  quote: IQuoteState;
  groupType: string;
  password: string;
  passwordConfirmation: string;
  policyDeclaration: boolean;
  errorMessage: string;
  paymentInfo: IPaymentInfo;
  actions: {
    submit(name: string);
    push: (route: string) => void;
    calculate: any;
  };
  schemeId: string;
  reuseCustomerAddress?: boolean;
}

class PaymentForm extends React.Component<IPaymentFormProps> {
  public componentDidMount() {
    const { reuseCustomerAddress, channel, change } = this.props;

    const valueNotSet = reuseCustomerAddress === undefined || reuseCustomerAddress === null;

    if (valueNotSet) {
      change('reuseCustomerAddress', channel.key === 'camc-b2c');
    }
  }

  public renderTravellersForInput = () => {
    const { i18n, product, groupType, renderDefinition, change } = this.props;

    return (
      <div>
        <div className="section-title">
          <h1>Traveller Details</h1>
        </div>
        <FieldArray
          name="travellers"
          i18n={i18n}
          product={product}
          component={Travellers}
          groupType={groupType}
          renderDefinition={renderDefinition}
          change={change}
        />
      </div>
    );
  }

  private renderTrackingItems = () => {
    const { branding, premium } = this.props;

    const items = [];

    if (branding.channel.channelType === 'AGG' && branding.aggregatorTracking.type === 'cyti' && premium) {
      const params = branding.aggregatorTracking.cyti;
      const cytiUrl = params.url + '/track/click';

      items.push(
        <img
          key={0}
          src={`${cytiUrl}?cref=${params.cref}&premium=${premium.gross}&t=${params.token}`}
          height="0"
          width="0"
        />,
      );
    }

    return items;
  }

  private renderPremium = () => {
    const { quote, schemeId } = this.props;

    let premium: IPremium = null;

    forEach(quote.quotes, (tripTypes) => {
      forEach(tripTypes, (quotedPremium) => {
        if (quotedPremium.scheme.id === schemeId) {
          premium = quotedPremium;
        }
      });
    });
    if (! premium) {
      return null;
    }

    return (
      <FormattedNumber
        value={premium.gross}
        style="currency"
        currency={premium.currency}
      />
    );
  }

  public renderPaymentForm = () => {
    const { channel } = this.props;
    const TERMS_OF_BUSINESS = 'Terms of Business';
    const aiosDocument = find(channel.productDocuments, (document) => {
      return document.shortName === TERMS_OF_BUSINESS;
    });
    const aboutUsUrl = aiosDocument ? aiosDocument.filePath : '';
    let privacyUrl = '/privacy-cookies';
    if ('fps-b2c' === channel.key) {
      privacyUrl = 'https://fpstravelcover.org.uk/privacy-cookies/';
    }
    if ('npo-b2c' === channel.key) {
      privacyUrl = 'https://www.narpotravelcover.org.uk/privacy-cookies/';
    }
    if ('cspa-b2c' === channel.key) {
      privacyUrl = get(channel, 'urls.privacyCookies', '');
    }
    if ('css-b2c' === channel.key) {
      privacyUrl = 'https://combinedservicestravelcover.org.uk/privacy-cookies/';
    }
    if ('cssr-b2c' === channel.key) {
      privacyUrl = 'https://combinedservicestravelcover.org.uk/privacy-cookies/';
    }
    if ('camc-b2c' === channel.key) {
      privacyUrl = 'https://caravanclubtravelcover.co.uk/privacy-cookies/';
    }
    if ('tpl-b2c' === channel.key) {
        privacyUrl = 'https://www.travelpluscover.co.uk/documents/Socprivacypolicy.pdf';
    }
    return (
      <div>
        <div className="section-title">
          <h1>Your Payment</h1>
        </div>
        <div className="section">
          <Row className="payment-links">
            For more information about our service to you and how we will use your information, please read our
            <a
                href={aboutUsUrl}
                target="_blank"
            > <strong>Terms of Business</strong>
            </a> and our <a
              href={privacyUrl}
              target="_blank"
            >
            <strong>Privacy Policy.</strong>
          </a>
          </Row>
        </div>
        <div className="section">
          <Row>
            <div className="internal-title">Payment Details</div>
            <div style={{ marginTop: '5px' }}>
              Total amount to pay: <strong>{this.renderPremium()}</strong>
            </div>
            <small>
              (inc. Insurance Premium Tax (IPT) if applicable)
            </small>
            <div className="margin-top">
              Once you are sure all your details are correct and the cover meets your needs, click 'BUY NOW' button
              where you will be prompted to enter your card details. <br/>
              {channel.key === 'camc-b2c' && (
                  <span>We accept all major debit and credit cards except American Express (AMEX). <br/></span>
              )}
              This is a secure payment process. If you have any questions, please give us a call.
            </div>
            <div className="margin-top">
              If you have any issues trying to purchase your policy,
              please call us on {channel.phone} (we are open {channel.openingHours}, closed bank
              holidays).
            </div>
          </Row>
        </div>
      </div>
    );
  }

  public renderPaymentBillingData = () => {
    const { i18n, reuseCustomerAddress, channel } = this.props;
    const defaultReuse = channel.key === 'camc-b2c';
    const effectiveReuse =
        reuseCustomerAddress === undefined || reuseCustomerAddress === null
            ? defaultReuse
            : reuseCustomerAddress;

    const showCardDetails = !effectiveReuse;

    return (
      <>
        <div className="payment-details-chk">
          <Row className="radio-horizontal">
            <Col sm={12}>
              <Field
                  name="reuseCustomerAddress"
                  type="checkbox"
                  component={FieldFormControl}
                  validate={[isRequired]}
              >
                <div/>
                Are card holder details same as yours?
              </Field>
            </Col>
          </Row>
        </div>

        {showCardDetails ? (
            <>
              <div className="margin-top">
                <Row>
                  <Col sm={4}>
                    Card Holder name
                  </Col>
                  <Col sm={4}>
                    <Field
                        name="payment.billing.name"
                        type="text"
                        placeholder="Cardholder name"
                        component={FieldFormControl}
                        validate={[isRequired]}
                        className="payment-field"
                    />
                  </Col>
                </Row>
              </div>
              <div className="margin-top">
                <Row>
                  <Col sm={4}>
                    Card Holder Email
                  </Col>
                  <Col sm={4}>
                    <Field
                        name="payment.billing.email"
                        type="text"
                        placeholder="Cardholder Email"
                        component={FieldFormControl}
                        validate={[isRequired, isEmail]}
                        className="payment-field"
                    />
                  </Col>
                </Row>
              </div>
              <div className="margin-top">
                <Row>
                  <Col sm={4}>
                    Card Holder Phone
                  </Col>
                  <Col sm={4}>
                    <Field
                        name="payment.billing.phone"
                        type="text"
                        placeholder="Cardholder Phone"
                        component={FieldFormControl}
                        validate={[isRequired, isPhone]}
                        className="payment-field"
                    />
                  </Col>
                </Row>
              </div>
              <div className="margin-top">
                <Row>
                  <Col sm={4}>
                    Card Holder Postcode
                  </Col>
                  <Col sm={4}>
                    <Field
                        name="payment.billing.address.postcode"
                        type="text"
                        placeholder={get(i18n, 'quote.tripDetails.traveller.postcode',
                            'Postcode')}
                        component={PostcodeControl}
                        onSelectAddress={this.handleSelectCardHolderAddress}
                        validate={[
                          isRequired,
                          isPostcode,
                        ]}
                    />
                  </Col>
                </Row>
              </div>
              <div className="margin-top">
                <Row>
                  <Col sm={6}>
                    <label>{get(i18n, 'quote.tripDetails.traveller.address', 'Card Holder Address')}</label>
                  </Col>
                </Row>
              </div>
              <div className="margin-top">
                <Row>
                  <Col sm={4}/>
                  <Col sm={4}>
                    <Field
                        className="line-top"
                        name="payment.billing.address.line1"
                        placeholder="Line 1"
                        type="text"
                        component={FieldFormControl}
                        validate={[
                          isRequired,
                        ]}
                    />
                    <Field
                        className="line-middle"
                        name="payment.billing.address.line2"
                        placeholder="Line 2"
                        type="text"
                        component={FieldFormControl}
                    />
                    <Field
                        className="line-middle"
                        name="payment.billing.address.town"
                        type="text"
                        placeholder="Town"
                        component={FieldFormControl}
                    />
                    <Field
                        className="line-bottom payment-field"
                        name="payment.billing.address.county"
                        placeholder="County"
                        type="text"
                        component={FieldFormControl}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}><hr /></Col>
                </Row>
              </div>
            </>
        ) : (
          <hr />
        )}
      </>
    );
  }

  private handleSelectCardHolderAddress = (address: IPostcodeAddress) => {
    const { change } = this.props;
    const base = 'payment.billing.address';

    change(`${base}.line1`, address.line1);
    change(`${base}.line2`, address.line2);
    change(`${base}.town`, address.town);
    change(`${base}.county`, address.county);
    change(`${base}.postcode`, address.postcode);
  }

  public render() {
    const {
      submitting,
      handleSubmit,
      quote,
      password,
      passwordConfirmation,
      errorMessage,
      channel,
      onBack,
    } = this.props;

    const isAgg = channel.channelType === 'AGG';
    const customer = quote.customer;
    const hasCustomer = !! customer;
    const disableSubmit = password !== passwordConfirmation;

    return (
      <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">
        <div className="steps-container">
          {isAgg && this.renderTravellersForInput()}
          {this.renderTrackingItems()}
          {this.renderPaymentForm()}
          {quote.errorMessage && (
            <Alert bsStyle={'danger'} style={{color: '#d64f48', marginBottom: 10}}>
              {errorMessage}
            </Alert>
          )}
          {this.renderPaymentBillingData()}
          {isAgg && (
            <div className="section">
              <Row>
                <MarketingOptions/>
              </Row>
            </div>
          )}
          <hr/>
          <div className="btn-bar">
            <Button
              bsStyle="primary"
              type="submit"
              className="pull-right btn-remove-border"
              disabled={
                submitting ||
                disableSubmit ||
                !hasCustomer
              }
            >
              Buy Now
            </Button>

            {! isAgg && (
              <Button type="button" className="btn-back" onClick={onBack}>
                Back
              </Button>
            )}
          </div>
        </div>
      </Form>
    );
  }
}

const validate = (values) => {
  const errors = {
    passwordConfirmation: undefined,
  };
  if (values.passwordConfirmation !== values.password) {
    errors.passwordConfirmation = 'Your confirmed password doesn\'t match the first field, please enter them again.';
  }
  return errors;
};

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    validate,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
    onSubmitFail: () => {
      setTimeout(() => scrollToFirstError({ offset: 120 }), 0);
    },
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const channel = state.branding.channel;
      const quotes = state.quote.quotes;
      const errorMessage = state.quote.errorMessage;
      const schemeId = selector(state, 'schemeId');
      const password = selector(state, 'password');
      const passwordConfirmation = selector(state, 'passwordConfirmation');
      const policyDeclaration = selector(state, 'policyDeclaration');
      const paymentInfo = selector(state, 'payment');
      const reuseCustomerAddress = selector(state, 'reuseCustomerAddress');

      let premium = null;

      forEach(quotes, (tripTypes, level) => {
        forEach(tripTypes, (quotedPremium) => {
          if (quotedPremium.scheme.id === schemeId) {
            premium = quotedPremium;
          }
        });
      });

      return {
        schemeId,
        premium,
        channel,
        policyDeclaration,
        password,
        passwordConfirmation,
        errorMessage,
        paymentInfo,
        reuseCustomerAddress,
      };
    },
    (dispatch) => ({
      actions: {
        dispatch,
        submit: bindActionCreators(submit, dispatch),
        calculate: bindActionCreators(actions.calculate, dispatch),
        push: bindActionCreators(push, dispatch),
      },
    }),
  ),
])(PaymentForm);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/scenes/PaymentB/index.tsx